// client/src/hooks/useTransportadores.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import useAlert from './useAlert';

const TRANSPORTADORES_QUERY_KEY = 'transportadores';
const TRANSPORTADOR_DETAIL_QUERY_KEY = 'transportador';

const sanitizeParams = (params) => {
    const sanitized = {};
    Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null && value !== '') {
            sanitized[key] = value;
        }
    });
    return sanitized;
};

export const useTransportadores = (params = {}) => {
    return useQuery({
        queryKey: [TRANSPORTADORES_QUERY_KEY, params],
        queryFn: async () => {
            const response = await api.get('/transportadores', { params: sanitizeParams(params) });
            const responseData = response.data;
            return {
                success: responseData?.success ?? true,
                data: Array.isArray(responseData?.data) ? responseData.data : [],
                count: responseData?.count ?? responseData?.total ?? 0,
                pagination: responseData?.pagination || {}
            };
        },
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
        retry: 1,
    });
};

export const useTransportador = (transportadorId, options = {}) => {
    return useQuery({
        queryKey: [TRANSPORTADOR_DETAIL_QUERY_KEY, transportadorId],
        queryFn: async () => {
            const response = await api.get(`/transportadores/${transportadorId}`);
            return response.data?.data || null;
        },
        enabled: !!transportadorId && (options.enabled !== undefined ? options.enabled : true),
        staleTime: 1000 * 60 * 5,
        retry: 1,
    });
};

export const useMutateTransportadores = () => {
    const queryClient = useQueryClient();
    const { setAlert } = useAlert();

    const invalidateCaches = (transportadorId = null) => {
        queryClient.invalidateQueries({ queryKey: [TRANSPORTADORES_QUERY_KEY] });
        if (transportadorId) {
            queryClient.invalidateQueries({ queryKey: [TRANSPORTADOR_DETAIL_QUERY_KEY, transportadorId] });
        }
    };

    const handleError = (error, defaultMessage) => {
        const errorMessage = error.response?.data?.error || error.response?.data?.message || defaultMessage;
        setAlert(errorMessage, 'error');
        throw new Error(errorMessage);
    };

    const addTransportador = useMutation({
        mutationFn: async (transportadorData) => {
            const response = await api.post('/transportadores', transportadorData);
            return response.data;
        },
        onSuccess: () => {
            invalidateCaches();
            setAlert('Transportador criado com sucesso!', 'success');
        },
        onError: (error) => handleError(error, 'Erro ao criar transportador'),
    });

    const updateTransportador = useMutation({
        mutationFn: async ({ id, transportadorData }) => {
            const response = await api.put(`/transportadores/${id}`, transportadorData);
            return response.data;
        },
        onSuccess: (response, variables) => {
            invalidateCaches(variables.id);
            setAlert('Transportador atualizado com sucesso!', 'success');
        },
        onError: (error) => handleError(error, 'Erro ao atualizar transportador'),
    });

    const deleteTransportador = useMutation({
        mutationFn: async (transportadorId) => {
            const response = await api.delete(`/transportadores/${transportadorId}`);
            return response.data;
        },
        onSuccess: () => {
            invalidateCaches();
            setAlert('Transportador removido com sucesso!', 'success');
        },
        onError: (error) => handleError(error, 'Erro ao remover transportador'),
    });

    const isPending = addTransportador.isPending || updateTransportador.isPending || deleteTransportador.isPending;

    return {
        addTransportador,
        updateTransportador,
        deleteTransportador,
        isPending,
    };
};

export const useTransportadorForm = (transportadorId = null) => {
    const { data: transportador, isLoading, error } = useTransportador(transportadorId);
    const { addTransportador, updateTransportador } = useMutateTransportadores();

    const salvarTransportador = async (transportadorData) => {
        if (transportadorId) {
            return updateTransportador.mutateAsync({ id: transportadorId, transportadorData });
        }
        return addTransportador.mutateAsync(transportadorData);
    };

    return {
        transportador,
        isLoading: isLoading || addTransportador.isPending || updateTransportador.isPending,
        error,
        salvarTransportador,
        isEditando: !!transportadorId,
    };
};

export default useTransportadores;
